import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, Text, View } from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';

import Breadcrumb from './components/breadcrumb';
import BoxPropList from './components/box_prop_list';
import ProgressSpinner from './components/progress_spinner';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';

import OrgStore from './stores/org_store';
import AppStore from './stores/app_store';
import UserStore from './stores/user_store';

const styles = StyleSheet.create({
  orgAppScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  spinner: { margin: '3rem', alignSelf: 'center' },
  dialog: { marginLeft: '2rem', marginBottom: '2rem', flexDirection: 'column' },
  text: { marginTop: '1em', fontSize: '1.5em' },
  button: { marginTop: '1rem', alignSelf: 'flex-start' },
});

export default function OrgAppScreen() {
  const params = useParams();
  const app_id = parseInt(params?.app_id ?? '');
  const [is_busy, setBusy, clearBusy] = useBusy();
  const org_id = UserStore.useOrg()?.org_id;
  const org = OrgStore.useOrg(org_id);
  const app = AppStore.useApp(org_id, app_id);
  const [updates, setUpdates] = React.useState({});
  const secret_key = AppStore.useKey(app_id);

  React.useEffect(() => {
    OrgStore.fetch(org_id);
    AppStore.fetch(org_id);
  }, [org_id]);

  function _onSave(complete) {
    if (setBusy()) {
      const opts: any = { org_id, app_id, ...updates };
      if (opts.is_disabled !== undefined) {
        opts.is_disabled = opts.is_disabled === 'Disabled' ? 1 : 0;
      }
      AppStore.update(opts, (err) => {
        clearBusy();
        if (err === 409) {
          window.alert('Name already in use.');
        } else if (err) {
          window.alert('Update failed, please try again.');
        } else {
          complete?.();
        }
      });
    }
  }
  function _onNewKeyPress() {
    const yes =
      !app.has_key ||
      window.confirm(
        'Are you sure you want generate a new key?  This will invalidate the existing key.'
      );
    if (yes && setBusy()) {
      AppStore.newKey({ org_id, app_id }, (err) => {
        clearBusy();
        if (err) {
          window.alert('Generate key failed, please try again.');
        }
      });
    }
  }

  let content: any = null;
  if (!org || !app) {
    content = <ProgressSpinner style={styles.spinner} color='#555' />;
  } else {
    const rows = app.device_list.map((device) => {
      return {
        url: `/device/${device.device_id}`,
        list: [device.device_name, device.is_disabled ? 'Disabled' : 'Enabled'],
      };
    });
    let empty: any = null;
    if (rows.length === 0) {
      empty = (
        <View style={styles.dialog}>
          <Text style={styles.text}>No devices associated with this app.</Text>
        </View>
      );
    }

    content = (
      <>
        <Breadcrumb
          list={[
            { text: org?.org_name, url: `/org` },
            { text: 'Apps', url: `/org/app` },
            app.app_name,
          ]}
        />
        <BoxPropList
          title={app?.app_name}
          editable={true}
          updates={updates}
          onChange={setUpdates}
          onSave={_onSave}
          columns={[
            [
              {
                key: 'app_name',
                label: 'App Name',
                value: app.app_name,
                input: { placeholder: 'Name' },
              },
              {
                key: 'is_disabled',
                label: 'Status',
                value: app.is_disabled ? 'Disabled' : 'Enabled',
                radio: { options: ['Enabled', 'Disabled'] },
              },
            ],
            [
              {
                label: 'Secret Key',
                value: app.has_key ? secret_key || '<...>' : 'None Assigned',
              },
            ],
          ]}
        />
        <StandardBoxList title='Secret Key'>
          <View style={styles.dialog}>
            <TextButton
              style={styles.button}
              text={app.has_key ? 'Regenerate Key' : 'New Key'}
              onPress={_onNewKeyPress}
            />
          </View>
        </StandardBoxList>
        <StandardBoxList
          title='Devices'
          headers={['Device Name', 'Status']}
          rows={rows}
        >
          {empty}
        </StandardBoxList>
        <BusyOverlay isBusy={is_busy} />
      </>
    );
  }
  return <View style={styles.orgAppScreen}>{content}</View>;
}
